/**
 * Language and locale utility functions
 * 
 * Provides helper functions for handling internationalization,
 * language codes, paths, and UI elements like flags and language switchers.
 */

export const SUPPORTED_LOCALES = ['en-us', 'de-de', 'es-es', 'fr-fr'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export const isSupportedLocale = (locale: string): locale is SupportedLocale => {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

/**
 * Converts a locale string to a short language code
 * 
 * @param locale - The full locale string (e.g., 'en-us', 'de-de')
 * @returns The short language code (e.g., 'en', 'de') or 'en' as fallback
 * 
 * @example
 * ```ts
 * getLang('en-us') // Returns: 'en'
 * getLang('de-de') // Returns: 'de'
 * getLang('invalid') // Returns: 'en' (fallback)
 * ```
 */
export const getLang = (locale: string) => {
  const obj: Record<SupportedLocale, string> = {
    'en-us': 'en',
    'de-de': 'de',
    'es-es': 'es',
    'fr-fr': 'fr',
  }

  return obj[locale as keyof typeof obj] || 'en'
}

/**
 * Gets the URL path prefix for a given locale
 * 
 * @param locale - The full locale string (e.g., 'en-us', 'de-de')
 * @returns The path prefix for the locale (e.g., 'de-de', 'es-es') or empty string for 'en-us'
 * 
 * @example
 * ```ts
 * getLangPath('en-us') // Returns: '' (no prefix for English)
 * getLangPath('de-de') // Returns: 'de-de'
 * getLangPath('es-es') // Returns: 'es-es'
 * ```
 */
export const getLangPath = (locale: string) => {
  const obj: Record<SupportedLocale, string> = {
    'en-us': '',
    'de-de': 'de-de',
    'es-es': 'es-es',
    'fr-fr': 'fr-fr',
  }

  return obj[locale as keyof typeof obj] || ''
}

/**
 * Gets the flag image path for a given locale
 * 
 * @param locale - The full locale string (e.g., 'en-us', 'de-de')
 * @returns The path to the flag SVG image for the locale, or undefined if not found
 * 
 * @example
 * ```ts
 * getFlag('en-us') // Returns: '/images/flag/us.svg'
 * getFlag('de-de') // Returns: '/images/flag/de.svg'
 * ```
 */
export const getFlag = (locale: string) => {
  const obj: Record<SupportedLocale, string> = {
    'en-us': '/images/flag/us.svg',
    'de-de': '/images/flag/de.svg',
    'es-es': '/images/flag/es.svg',
    'fr-fr': '/images/flag/fr.svg',
  }

  return obj[locale as keyof typeof obj]
}

/**
 * Gets the display text for the language switcher
 * 
 * @param locale - The full locale string (e.g., 'en-us', 'de-de')
 * @returns The formatted display text combining country and language name
 * 
 * @example
 * ```ts
 * getSwitcherLang('en-us') // Returns: 'US / English'
 * getSwitcherLang('de-de') // Returns: 'Germany / Deutsch'
 * getSwitcherLang('fr-fr') // Returns: 'France / Française'
 * ```
 */
export const getSwitcherLang = (locale: string) => {
  const obj: Record<SupportedLocale, string> = {
    'en-us': 'US / English',
    'de-de': 'Germany / Deutsch',
    'es-es': 'Spain / Español',
    'fr-fr': 'France / Française',
  }

  return obj[locale as keyof typeof obj]
}
